export default {
  name: 'responsiveTextStyle',
  magic: (el, { Alpine }) => {
    // Get breakpoints from global window object (set in head-scripts.liquid)
    const getBreakpoints = () => {
      if (window.responsiveBreakpoints) {
        return {
          mobile: window.responsiveBreakpoints.mobile, // max-width for mobile
          tablet: window.responsiveBreakpoints.tablet, // max-width for tablet  
          desktop: window.responsiveBreakpoints.desktop, // max-width for desktop
          ultrawide: window.responsiveBreakpoints.ultrawide // min-width for ultrawide
        };
      }
      
      // Fallback to default values if global breakpoints are not available
      return {
        mobile: 767,
        tablet: 1023,
        desktop: 1439,
        ultrawide: 1440
      };
    };

    // Create a reactive store that Alpine can watch
    const deviceStore = Alpine.reactive({
      currentDevice: 'desktop',
      lastUpdate: Date.now()
    });

    // Debounce function to limit resize event frequency
    const debounce = (func, wait) => {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    };

    // Update current device based on window width
    const updateCurrentDevice = () => {
      const breakpoints = getBreakpoints();
      const width = window.innerWidth;
      const oldDevice = deviceStore.currentDevice;

      if (width <= breakpoints.mobile) {
        deviceStore.currentDevice = 'mobile';
      } else if (width <= breakpoints.tablet) {
        deviceStore.currentDevice = 'tablet';
      } else if (width >= breakpoints.ultrawide) {
        deviceStore.currentDevice = 'ultrawide';
      } else {
        deviceStore.currentDevice = 'desktop';
      }

      if (oldDevice !== deviceStore.currentDevice) {
        deviceStore.lastUpdate = Date.now(); // Trigger reactivity
        
        // Force Alpine to re-evaluate all reactive expressions
        document.querySelectorAll('[x-data], [\\:class*="$responsiveTextStyle"]').forEach(element => {
          if (element._x_dataStack) {
            Alpine.flushAndStopDeferringMutations();
          }
        });
      }
    };

    // Debounced resize handler
    const debouncedResize = debounce(updateCurrentDevice, 150);

    // Initialize current device
    updateCurrentDevice();

    // Set up resize listener (only once globally)
    if (!window.__responsiveTextStyleInitialized) {
      window.addEventListener('resize', debouncedResize);
      window.__responsiveTextStyleInitialized = true;
      
      // Store the device store globally so all instances can access it
      window.__responsiveTextStyleStore = deviceStore;
    } else {
      // Use the existing global store
      Object.assign(deviceStore, window.__responsiveTextStyleStore);
    }

    // Main function that returns the appropriate text style
    return (
      textStyle,
      textStyleMobile = null,
      textStyleTablet = null,
      textStyleUltrawide = null,
      enableResponsive = true
    ) => {
      // Access the reactive store to trigger Alpine's dependency tracking
      const currentDevice = deviceStore.currentDevice;
      const _ = deviceStore.lastUpdate; // This creates a dependency on updates

      // Remove the default class that was applied server-side to prevent flash
      // Only do this once when Alpine initializes
      if (el && !el.hasAttribute('data-responsive-initialized')) {
        // Remove the default text style class that was applied server-side
        el.classList.remove(textStyle);
        if (textStyleMobile) el.classList.remove(textStyleMobile);
        if (textStyleTablet) el.classList.remove(textStyleTablet);
        if (textStyleUltrawide) el.classList.remove(textStyleUltrawide);
        el.setAttribute('data-responsive-initialized', 'true');
      }

      // If responsive is disabled, always return the desktop/base style
      if (!enableResponsive) {
        return textStyle;
      }

      // Return appropriate text style based on current device and available styles
      switch (currentDevice) {
        case 'mobile':
          return textStyleMobile || textStyle;
        case 'tablet':
          return textStyleTablet || textStyle;
        case 'ultrawide':
          return textStyleUltrawide || textStyle;
        default:
          return textStyle;
      }
    };
  }
};
